import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import RecipeSelectionModal from './RecipeSelectionModal';

interface Recipe {
  id: number;
  name: string;
  ingredients: string[];
  instructions: string[];
}

interface MealPlan {
  [date: string]: {
    breakfast: Recipe | null;
    lunch: Recipe | null;
    dinner: Recipe | null;
  };
}

const getWeekDays = (startDate: Date) => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    days.push(date.toISOString().split('T')[0]); // Format YYYY-MM-DD
  }
  return days;
};

const WeeklyOverview: React.FC = () => {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 for Sunday, 1 for Monday, etc.
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust to Monday of the current week
    return new Date(today.setDate(diff));
  });

  const [days, setDays] = useState<string[]>([]);
  const [allRecipes, setAllRecipes] = useState<Recipe[]>([]);
  const [showBreakfast, setShowBreakfast] = useState<boolean>(false);
  const [showRecipeModal, setShowRecipeModal] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | null>(null);

  const [mealPlan, setMealPlan] = useState<MealPlan>({});

  useEffect(() => {
    axios.get('http://localhost:8000/api/recipes')
      .then(response => setAllRecipes(response.data))
      .catch(error => console.error('Error fetching recipes:', error));
  }, []);

  useEffect(() => {
    const fetchMealPlan = async () => {
      const currentDays = getWeekDays(currentWeekStart);
      setDays(currentDays);
      const newMealPlan: MealPlan = {};
      for (const day of currentDays) {
        try {
          const response = await axios.get(`http://localhost:8000/api/daily_selections/${day}`);
          newMealPlan[day] = response.data;
        } catch (error) {
          console.error(`Error fetching meal plan for ${day}:`, error);
          newMealPlan[day] = { breakfast: null, lunch: null, dinner: null };
        }
      }
      setMealPlan(newMealPlan);
    };
    fetchMealPlan();
  }, [currentWeekStart]);

  

  const handleAddRecipeToMealPlan = async (recipe: Recipe) => {
    if (selectedDate && selectedMealType) {
      try {
        const response = await axios.post('http://localhost:8000/api/daily_selections', {
          date: selectedDate,
          meal_type: selectedMealType,
          recipe_id: recipe.id
        });
        if (response.status === 200) {
          setMealPlan(prevPlan => ({
            ...prevPlan,
            [selectedDate]: {
              ...prevPlan[selectedDate],
              [selectedMealType]: recipe
            }
          }));
        }
      } catch (error) {
        console.error('Error adding recipe to meal plan:', error);
      }
      setShowRecipeModal(false);
      setSelectedDate(null);
      setSelectedMealType(null);
    }
  };

  const openRecipeModal = (date: string, mealType: 'breakfast' | 'lunch' | 'dinner') => {
    setSelectedDate(date);
    setSelectedMealType(mealType);
    setShowRecipeModal(true);
  };

  const handleRemoveRecipeFromMealPlan = async (day: string, mealType: 'breakfast' | 'lunch' | 'dinner') => {
    try {
      const response = await axios.delete(`http://localhost:8000/api/daily_selections/${day}`, { data: { meal_type: mealType } });
      if (response.status === 200) {
        setMealPlan(prevPlan => ({
          ...prevPlan,
          [day]: {
            ...prevPlan[day],
            [mealType]: null
          }
        }));
      }
    } catch (error) {
      console.error('Error removing recipe from meal plan:', error);
    }
  };

  return (
    <div className="container mt-5">
      <h1 className="mb-4">Weekly Meal Overview</h1>
      <Link to="/" className="btn btn-primary mb-4">Back to Home</Link>

      <div className="mb-4 text-center">
        <h2 className="mb-2">Week of {currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</h2>
        <div className="d-flex justify-content-center">
          <button className="btn btn-secondary mx-2" onClick={() => {
            const newDate = new Date(currentWeekStart);
            newDate.setDate(currentWeekStart.getDate() - 7);
            setCurrentWeekStart(newDate);
          }}>&larr;</button>
          <button className="btn btn-secondary mx-2" onClick={() => {
            const today = new Date();
            const dayOfWeek = today.getDay();
            const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            setCurrentWeekStart(new Date(today.setDate(diff)));
          }}>&#x2022;</button>
          <button className="btn btn-secondary mx-2" onClick={() => {
            const newDate = new Date(currentWeekStart);
            newDate.setDate(currentWeekStart.getDate() + 7);
            setCurrentWeekStart(newDate);
          }}>&rarr;</button>
        </div>
      </div>

      

      

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Day</th>
              {showBreakfast && <th>Breakfast</th>}
              <th>Lunch</th>
              <th>Dinner</th>
            </tr>
          </thead>
          <tbody>
            {days.map(date => (
              <tr key={date}>
                <td>{new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'numeric', day: 'numeric' })}</td>
                {showBreakfast && (
                  <td>
                  {mealPlan[date]?.breakfast ? (
                    <>
                      <Link to={`/recipe/${mealPlan[date].breakfast?.id}`}>{mealPlan[date].breakfast?.name}</Link>
                      <button className="btn btn-danger btn-sm ms-2" onClick={() => handleRemoveRecipeFromMealPlan(date, 'breakfast')}>X</button>
                    </>
                  ) : (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => openRecipeModal(date, 'breakfast')}>Add</button>
                  )}
                </td>
                )}
                <td>
                  {mealPlan[date]?.lunch ? (
                    <>
                      <Link to={`/recipe/${mealPlan[date].lunch?.id}`}>{mealPlan[date].lunch?.name}</Link>
                      <button className="btn btn-danger btn-sm ms-2" onClick={() => handleRemoveRecipeFromMealPlan(date, 'lunch')}>X</button>
                    </>
                  ) : (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => openRecipeModal(date, 'lunch')}>Add</button>
                  )}
                </td>
                <td>
                  {mealPlan[date]?.dinner ? (
                    <>
                      <Link to={`/recipe/${mealPlan[date].dinner?.id}`}>{mealPlan[date].dinner?.name}</Link>
                      <button className="btn btn-danger btn-sm ms-2" onClick={() => handleRemoveRecipeFromMealPlan(date, 'dinner')}>X</button>
                    </>
                  ) : (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => openRecipeModal(date, 'dinner')}>Add</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="form-check form-switch mt-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="flexSwitchCheckDefault"
          checked={showBreakfast}
          onChange={() => setShowBreakfast(!showBreakfast)}
        />
        <label className="form-check-label" htmlFor="flexSwitchCheckDefault">Show Breakfast</label>
      </div>

      <RecipeSelectionModal
        show={showRecipeModal}
        onHide={() => setShowRecipeModal(false)}
        recipes={allRecipes}
        onSelectRecipe={handleAddRecipeToMealPlan}
      />
    </div>
  );
};

export default WeeklyOverview;
